/* global Sys, Core */
Sys.ns("Core");

/**
 * @typedef {(string|Object)} Mixin
 */

/**
 * The base Module class.
 *
 * This class should be used as a base when creating new modules.
 *
 * Read the guides for information on how to set up a new module.
 *
 * @class Core.Module
 * @extends Sys.Observable
 * @aside guide modules__modules
 * @aside guide state_handler__intro
 * @aside guide state_handler__module
 */
Core.Module = {

    /**
     * @cfg {String} name
     * The name of the Module
     */

    /**
     * @cfg {*} [controller] (optional)
     * The controller class the module uses.
     */

    /**
     * @cfg {*} [model] (optional)
     * The model class the module uses.
     */

    /**
     * @cfg {*} [view] (optional)
     * The view class the module uses.
     */

    /**
     * @override
     * @constructs
     * @param {Object} config The configuration object.
     */
    constructor: function(config) {
        Core.Module.superclass.constructor.apply(this, arguments);

        this.init(config);
    },

    /**
     * This method defines how the module affects the rest of the actual game flow.
     *
     * Returns the module state changes.
     *
     * @example
     * **Modify an existing state**
     *
     * To modify a state the returning object shall have a key named the same as the existing state.
     * The value of that key shall be an object that must contain the key `queue` and can optionally contain the key `waitEvents`.
     *
     * * `queue` defines an array of functions that will run when the states execute function has been performed
     * * `waitEvents` defines an array of events fired by the module that the state needs to have received before
     * the state is considered to be finished.
     *
     * An example that will modify the state named `existingStateName`:
     *
     *     {
     *         "existingStateName" : {
     *             "queue" : [
     *                 function (env) {}
     *              ],
     *              "waitEvents" : [
     *                  "firstEventName",
     *                  "secondEventName"
     *              ]
     *         }
     *     }
     *
     * This example will add two new waitEvents to `existingStateName` and the state handler will now wait for the
     * two more events before leaving the state.
     *
     * **Creating a new state**
     *
     * To create a new state the returning object shall have a key that is named as the new state.
     * The value of the key shall be an object that must contain the key `state`.
     * The value of state shall be an object with the keys `name`, `execute` amd `waitEvents`.
     *
     * * `name` defines the name of the state with with first letter capitalized
     * * `execute` defines the function that is executed when the state is executed.
     * * `waitEvents` defines an object where the key is the name of the event and the value shall be false.
     *
     * An example that will create the new state `mywState`:
     *
     *     {
     *         "myState" : {
     *             "state" : {
     *                 "name" : "MyState",
     *                 "execute" : function (env) {
     *                     console.log("event executed");
     *                 },
     *                 "waitEvents" : {
     *                     "notify:myModule:done" : false
     *                 }
     *             }
     *         }
     *     }
     *
     * Then add the module to the state order.
     *
     * When the state gets executed it will log to the console and the state handler will leave the state when the `notify:myModule:done` event is fired.
     *
     * @protected
     * @returns {Object} State changes.
     */
    getStateChanges: function() {
        return {};
    },

    /**
     * Gets the mixin dependencies for the module.
     *
     * This method should be overridden if a module uses any mixins.
     * The default behavior is to not implement any mixins.
     *
     * The array contains the mixins used, either on the form of strings or objects.
     * The object contains the name of the mixin as a key and the configuration as an object.
     * The configuration is passed as an argument to all methods that are executed on instantiation.
     *
     * @example
     * For example you could make the module handle animations and listen to user input,
     * by making this function return the following array:
     *
     *     [
     *         "animation",
     *         {
     *             "userInput" : {
     *                 "listenToUserInput" : true
     *             }
     *         }
     *     ]
     *
     * @protected
     * @returns {Mixin[]} The array containing the mixins that shall be implemented.
     */
    getMixinDependencies: function() {
        return [];
    },

    /**
     * Gets the default MVC classes that the module uses.
     *
     * This method is called by the init method when the Module Loader instantiates all the modules.
     * It returns an object containing three properties; model, view and controller, which defines the classes to be
     * used for each MVC component.
     *
     * This method should be overridden when creating a new module and return the controller, view and model that
     * shall be used. A module only has to contain a controller, but in many cases it will also include a model and a
     * view.
     *
     * @example
     *     {
     *         "controller" : MyNameSpace.MyModuleController,
     *         "view"       : MyNameSpace.MyModuleView,
     *         "model"      : MyNameSpace.MyModuleModel
     *     }
     *
     * @protected
     * @returns {{model: *, view: *, controller: *}} The default MVC classes.
     */
    getDefaultMVCClasses: function() {
        return {
            controller: Core.Controller
        };
    },

    /**
     * Initializes the module.
     *
     * @private
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    init: function(config) {
        var classTypes = ["model", "view", "controller"],
            defaultMVCClasses = this.getDefaultMVCClasses(),
            MVCClassTypes = Object.keys(defaultMVCClasses),
            eventHandler = new Sys.EventHandler(),
            Model,
            View,
            Controller;

        // Get classes from config if there are any
        Sys.each(classTypes, function(classType) {
            if (config[classType]) {
                defaultMVCClasses[classType] = config[classType];
            }
            else if (Sys.contains(MVCClassTypes, classType) && !defaultMVCClasses[classType]) {
                throw new Error("Module :: The " + config.name + " module has a " + classType + " class defined that is not included");
            }
        });

        // Only create a model if it is needed
        if (defaultMVCClasses.model) {
            Model = defaultMVCClasses.model;
            this.model = new Model({
                name: config.name,
                eventHandler: eventHandler
            });
        }

        // Only create a view if it is needed
        if (defaultMVCClasses.view) {
            // Create a model if one does not exist,
            // since the view depends on it for communication.
            if (!this.model) {
                this.model = new Core.Model({
                    name: config.name,
                    eventHandler: eventHandler
                });
            }

            View = defaultMVCClasses.view;
            this.view = new View({
                name: config.name,
                model: this.model,
                controller: this.controller,
                eventHandler: eventHandler
            });
        }

        Controller = defaultMVCClasses.controller;
        this.controller = new Controller({
            name: config.name,
            view: this.view,
            model: this.model,
            eventHandler: eventHandler
        });

        this.MODULE_NAME = config.name;
    }
};

Core.Module = Sys.extend(Sys.Observable, Core.Module, "Core.Module");
